import React, { useState, useMemo, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO, isBefore, isToday, addMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Wallet, Plus, Search, Trash2, Edit2, Loader2, Calendar,
  AlertTriangle, Filter, BarChart3, Repeat,
  ArrowUpDown, CalendarDays
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ContaForm, { categorias } from "../components/contas/ContaForm";
import FluxoCaixaFuturo from "../components/contas/FluxoCaixaFuturo";
import LembretesPanel from "../components/contas/LembretesPanel";
import CalendarioContas from "../components/contas/CalendarioContas";
import Paginacao from "../components/shared/Paginacao";

export default function ContasPagar() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const urlParams = new URLSearchParams(window.location.search);
  const initialQ = urlParams.get('q') || "";
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConta, setEditingConta] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialQ);
  const [filterStatus, setFilterStatus] = useState("pendentes");
  const [filterCategoria, setFilterCategoria] = useState("todas");
  const [sortBy, setSortBy] = useState("vencimento");
  const [activeTab, setActiveTab] = useState("lista");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [settleConta, setSettleConta] = useState(null);
  const [settleData, setSettleData] = useState({ juros: "0", multa: "0", desconto: "0", forma_pagamento: "pix", data_pagamento: new Date().toISOString().split('T')[0], conta_bancaria_id: "", comprovante_url: "", observacao: "" });
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundConta, setRefundConta] = useState(null);
  const [refundMotivo, setRefundMotivo] = useState("");
  const [refundConfirm, setRefundConfirm] = useState(false);

  const queryClient = useQueryClient();

  const { data: contas = [], isLoading } = useQuery({
    queryKey: ['contas_pagar', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaPagar.filter({ propriedade_id: propriedadeId }, '-data_vencimento', 500)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: contasBancarias = [] } = useQuery({
    queryKey: ['contas_bancarias', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaBancaria.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: lancamentos = [] } = useQuery({
    queryKey: ['lancamentos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Lancamento.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  // Calcular saldo atual
  const saldoAtual = useMemo(() => {
    const receitas = lancamentos.filter(l => l.tipo === 'receita').reduce((acc, l) => acc + (l.valor || 0), 0);
    const despesas = lancamentos.filter(l => l.tipo === 'despesa').reduce((acc, l) => acc + (l.valor || 0), 0);
    return receitas - despesas;
  }, [lancamentos]);

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.ContaPagar.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contas_pagar'] })
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => agrogestao.entities.ContaPagar.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contas_pagar'] })
  });

  const getLancamentoRelacionadoPagar = (conta) => {
    if (!conta || conta.status !== 'pago' || !conta.data_pagamento) return null;
    const liquido = (conta.valor || 0) + (conta.juros || 0) + (conta.multa || 0) - (conta.desconto || 0);
    const byRel = lancamentos.find(l => String(l.conta_pagar_id) === String(conta.id));
    if (byRel) return byRel;
    return lancamentos.find(l =>
      l.tipo === 'despesa' &&
      l.propriedade_id === propriedadeId &&
      Math.abs((l.valor || 0) - liquido) < 0.001 &&
      (l.descricao || '').startsWith(conta.descricao || '') &&
      (l.fornecedor_cliente || '') === (conta.fornecedor || '') &&
      l.data === conta.data_pagamento
    ) || null;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Atualizar status das contas atrasadas
  const contasProcessadas = useMemo(() => {
    const hoje = new Date();
    return contas.map(c => {
      const vencimento = parseISO(c.data_vencimento);
      const atrasada = c.status !== 'pago' && isBefore(vencimento, hoje) && !isToday(vencimento);
      return {
        ...c,
        status: atrasada ? 'atrasado' : c.status,
        vencimento
      };
    });
  }, [contas]);

  // Filtros e ordenação
  const contasFiltradas = useMemo(() => {
    let filtered = contasProcessadas;

    // Filtro de busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.descricao?.toLowerCase().includes(term) ||
        c.fornecedor?.toLowerCase().includes(term)
      );
    }

    // Filtro de status
    if (filterStatus === "pendentes") {
      filtered = filtered.filter(c => c.status !== 'pago');
    } else if (filterStatus !== "todas") {
      filtered = filtered.filter(c => c.status === filterStatus);
    }

    // Filtro de categoria
    if (filterCategoria !== "todas") {
      filtered = filtered.filter(c => c.categoria === filterCategoria);
    }

    // Ordenação
    filtered.sort((a, b) => {
      if (sortBy === "vencimento") {
        return a.vencimento - b.vencimento;
      } else if (sortBy === "valor") {
        return b.valor - a.valor;
      }
      return 0;
    });

    return filtered;
  }, [contasProcessadas, searchTerm, filterStatus, filterCategoria, sortBy]);

  // Paginação
  const contasPaginadas = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return contasFiltradas.slice(start, start + pageSize);
  }, [contasFiltradas, currentPage, pageSize]);

  // Reset page quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterCategoria]);

  // Totais
  const totais = useMemo(() => {
    const pendentes = contasProcessadas.filter(c => c.status !== 'pago');
    return {
      total: pendentes.reduce((acc, c) => acc + (c.valor || 0), 0),
      atrasadas: pendentes.filter(c => c.status === 'atrasado').reduce((acc, c) => acc + (c.valor || 0), 0),
      quantidade: pendentes.length,
      qtdAtrasadas: pendentes.filter(c => c.status === 'atrasado').length
    };
  }, [contasProcessadas]);

  const handleEdit = (conta) => {
    if (conta.status === 'pago') return;
    const original = contas.find(c => String(c.id) === String(conta.id));
    const contaNormalizada = {
      ...(original || conta),
      status: (original?.status || conta.status) === 'atrasado' ? 'pendente' : (original?.status || conta.status)
    };
    setEditingConta(contaNormalizada);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingConta(null);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['contas_pagar'] });
    setDialogOpen(false);
    setEditingConta(null);
  };



  const abrirQuitacao = (conta) => {
    setSettleConta(conta);
    setSettleData({ juros: "0", multa: "0", desconto: "0", forma_pagamento: conta.forma_pagamento || "pix", data_pagamento: new Date().toISOString().split('T')[0], conta_bancaria_id: conta.conta_bancaria_id || "", comprovante_url: conta.comprovante_url || "", observacao: "" });
    setSettleDialogOpen(true);
  };

  const handleComprovanteUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingComprovante(true);
    try {
      const { file_url } = await agrogestao.integrations.Core.UploadFile({ file });
      setSettleData(prev => ({ ...prev, comprovante_url: file_url }));
    } finally {
      setUploadingComprovante(false);
      e.target.value = "";
    }
  };

  const confirmarQuitacao = async () => {
    if (!settleConta) return;
    const juros = parseFloat(settleData.juros) || 0;
    const multa = parseFloat(settleData.multa) || 0;
    const desconto = parseFloat(settleData.desconto) || 0;
    const dataPag = settleData.data_pagamento || new Date().toISOString().split('T')[0];

    await updateMutation.mutateAsync({
      id: settleConta.id,
      data: {
        status: 'pago',
        data_pagamento: dataPag,
        juros,
        multa,
        desconto,
        conta_bancaria_id: settleData.conta_bancaria_id || null,
        forma_pagamento: settleData.forma_pagamento,
        comprovante_url: settleData.comprovante_url || null
      }
    });

    const totalPago = (settleConta.valor || 0) + juros + multa - desconto;


    if (settleConta.recorrente && settleConta.frequencia_recorrencia) {
      const mesesAdicionar = {
        semanal: 0.25,
        quinzenal: 0.5,
        mensal: 1,
        bimestral: 2,
        trimestral: 3,
        semestral: 6,
        anual: 12
      };
      const meses = mesesAdicionar[settleConta.frequencia_recorrencia] || 1;
      const novaData = addMonths(settleConta.vencimento, meses);

      await agrogestao.entities.ContaPagar.create({
        propriedade_id: settleConta.propriedade_id,
        descricao: settleConta.descricao,
        valor: settleConta.valor,
        data_vencimento: format(novaData, 'yyyy-MM-dd'),
        categoria: settleConta.categoria,
        fornecedor: settleConta.fornecedor,
        status: 'pendente',
        recorrente: true,
        frequencia_recorrencia: settleConta.frequencia_recorrencia,
        lembrete_dias: settleConta.lembrete_dias,
        forma_pagamento: settleConta.forma_pagamento,
        safra_id: settleConta.safra_id
      });
    }

    // AUTOMATIC BANK SETTLEMENT LOGIC
    if (settleData.conta_bancaria_id) {
      try {
        const contasBanco = await agrogestao.entities.ContaBancaria.filter({ id: settleData.conta_bancaria_id });
        const contaBanco = contasBanco[0];

        if (contaBanco) {
          // 1. Update Balance
          const novoSaldo = (contaBanco.saldo_atual || 0) - totalPago;
          await agrogestao.entities.ContaBancaria.update(contaBanco.id, { saldo_atual: novoSaldo });

          // 2. Create Statement Entry
          await agrogestao.entities.ExtratoBancario.create({
            propriedade_id: settleConta.propriedade_id,
            conta_bancaria_id: contaBanco.id,
            conta_pagar_id: settleConta.id,
            data: dataPag,
            descricao: `Pagamento: ${settleConta.descricao} (${settleConta.fornecedor || ''})`,
            valor: totalPago,
            tipo: 'debito'
          });
        }
      } catch (error) {
        console.error("Erro ao atualizar saldo bancário", error);
        // Non-blocking error, but could ideally verify transaction integrity
      }
    }

    setSettleDialogOpen(false);
    setSettleConta(null);
    queryClient.invalidateQueries({ queryKey: ['contas_pagar'] });
    queryClient.invalidateQueries({ queryKey: ['lancamentos'] });
  };

  const getStatusBadge = (status) => {
    const config = {
      pago: { label: "Pago", class: "bg-green-100 text-green-700" },
      pendente: { label: "Pendente", class: "bg-blue-100 text-blue-700" },
      atrasado: { label: "Atrasado", class: "bg-red-100 text-red-700" },
      agendado: { label: "Agendado", class: "bg-purple-100 text-purple-700" }
    };
    const c = config[status] || config.pendente;
    return <Badge className={c.class}>{c.label}</Badge>;
  };

  const getCategoriaLabel = (cat) => {
    return categorias.find(c => c.value === cat)?.label || cat;
  };

  const getCategoriaIcon = (cat) => {
    return categorias.find(c => c.value === cat)?.icon || "📦";
  };

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <Wallet className="w-8 h-8 text-red-600" />
            Contas a Pagar
          </h1>
          <p className="text-stone-600 mt-1">Gerencie seus compromissos financeiros</p>
        </div>

        <Button onClick={handleNew} className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2">
          <Plus className="w-5 h-5" />
          Nova Conta
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <p className="text-blue-100 text-sm">Total Pendente</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(totais.total)}</p>
            <p className="text-xs text-blue-200 mt-1">{totais.quantidade} conta(s)</p>
          </CardContent>
        </Card>

        <Card className={`${totais.atrasadas > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-green-500 to-green-600'} text-white`}>
          <CardContent className="p-4">
            <p className="text-white/80 text-sm">Atrasadas</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(totais.atrasadas)}</p>
            <p className="text-xs text-white/70 mt-1">{totais.qtdAtrasadas} conta(s)</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] text-white">
          <CardContent className="p-4">
            <p className="text-white/70 text-sm">Saldo Atual</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(saldoAtual)}</p>
            <p className="text-xs text-white/60 mt-1">Receitas - Despesas</p>
          </CardContent>
        </Card>

        <Card className={`${saldoAtual - totais.total >= 0 ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gradient-to-br from-amber-500 to-amber-600'} text-white`}>
          <CardContent className="p-4">
            <p className="text-white/80 text-sm">Saldo Projetado</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(saldoAtual - totais.total)}</p>
            <p className="text-xs text-white/70 mt-1">Após pagamentos</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="lista" className="gap-2">
            <Wallet className="w-4 h-4" />
            Lista de Contas
          </TabsTrigger>
          <TabsTrigger value="calendario" className="gap-2">
            <CalendarDays className="w-4 h-4" />
            Calendário
          </TabsTrigger>
          <TabsTrigger value="lembretes" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Lembretes
          </TabsTrigger>
          <TabsTrigger value="fluxo" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Fluxo Futuro
          </TabsTrigger>
        </TabsList>

        {/* Lista de Contas */}
        <TabsContent value="lista" className="mt-6 space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  placeholder="Buscar por descrição ou fornecedor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendentes">Pendentes</SelectItem>
                    <SelectItem value="todas">Todas</SelectItem>
                    <SelectItem value="atrasado">Atrasadas</SelectItem>
                    <SelectItem value="pago">Pagas</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas categorias</SelectItem>
                    {categorias.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-36">
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vencimento">Vencimento</SelectItem>
                    <SelectItem value="valor">Valor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Lista */}
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            {contasFiltradas.length === 0 ? (
              <div className="p-12 text-center">
                <Wallet className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nenhuma conta encontrada</p>
              </div>
            ) : (
              <>
                <div className="p-3 border-b border-stone-100 text-sm text-stone-500">{contasFiltradas.length} conta(s) encontrada(s)</div>
                <div className="divide-y divide-stone-100">
                  <AnimatePresence>
                    {contasPaginadas.map((conta, index) => (
                      <motion.div
                        key={conta.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ delay: index * 0.02 }}
                        className={`p-4 hover:bg-stone-50 transition-colors ${conta.status === 'atrasado' ? 'bg-red-50/50' : ''}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-2xl">{getCategoriaIcon(conta.categoria)}</div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-stone-800 truncate" title={conta.descricao}>{conta.descricao}</p>
                              {getStatusBadge(conta.status)}
                              {conta.recorrente && (
                                <Badge variant="outline" className="gap-1">
                                  <Repeat className="w-3 h-3" />
                                  {conta.frequencia_recorrencia}
                                </Badge>
                              )}
                              {conta.parcela_atual && conta.total_parcelas && (
                                <Badge variant="secondary">
                                  {conta.parcela_atual}/{conta.total_parcelas}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-stone-500 mt-1">
                              {getCategoriaLabel(conta.categoria)}
                              {conta.fornecedor && ` • ${conta.fornecedor}`}
                            </p>
                          </div>

                          <div className="text-right">
                            <p className="text-lg font-bold text-stone-800">{formatCurrency(conta.valor)}</p>
                            {conta.status === 'pago' ? (
                              <p className="text-sm text-green-700">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                Pago em {format(parseISO(conta.data_pagamento || conta.data_vencimento), "dd/MM/yyyy")}
                              </p>
                            ) : (
                              <p className={`text-sm ${conta.status === 'atrasado' ? 'text-red-600 font-medium' : 'text-stone-500'}`}>
                                <Calendar className="w-3 h-3 inline mr-1" />
                                Vence em {format(conta.vencimento, "dd/MM/yyyy")}
                              </p>
                            )}
                            {conta.status === 'pago' && (
                              <div className="mt-1 flex justify-end gap-1">
                                {conta.forma_pagamento && (
                                  <Badge variant="outline" className="text-xs">{conta.forma_pagamento}</Badge>
                                )}
                                {conta.conta_bancaria_id && (() => {
                                  const cb = contasBancarias.find(cb => String(cb.id) === String(conta.conta_bancaria_id));
                                  return cb ? <Badge variant="outline" className="text-xs">{cb.banco} • {cb.numero}</Badge> : null;
                                })()}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-1">
                            {conta.status !== 'pago' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => abrirQuitacao(conta)}
                                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                              >
                                Quitar
                              </Button>
                            )}
                            {conta.status === 'pago' && (() => {
                              const rel = getLancamentoRelacionadoPagar(conta);
                              return (
                                <>
                                  {rel ? (
                                    <Link
                                      to={`${createPageUrl('Financeiro')}?lancamento_id=${rel.id}`}
                                      className="text-[#40916C] text-sm px-2 py-1 hover:text-[#2D6A4F]"
                                      title="Ver lançamento"
                                    >
                                      Ver lançamento
                                    </Link>
                                  ) : null}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setRefundConta(conta); setRefundMotivo(""); setRefundConfirm(false); setRefundDialogOpen(true); }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    Estornar
                                  </Button>
                                </>
                              );
                            })()}
                            {conta.status !== 'pago' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(conta)}
                                className="text-stone-400 hover:text-stone-600"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            {conta.status !== 'pago' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteMutation.mutate(conta.id)}
                                className="text-stone-400 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          {conta.status === 'pago' && (
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                              <Badge variant="outline" className="bg-stone-50 text-stone-700">Juros: {formatCurrency(conta.juros || 0)}</Badge>
                              <Badge variant="outline" className="bg-stone-50 text-stone-700">Multa: {formatCurrency(conta.multa || 0)}</Badge>
                              <Badge variant="outline" className="bg-stone-50 text-stone-700">Desconto: {formatCurrency(conta.desconto || 0)}</Badge>
                              <Badge variant="outline" className="bg-stone-50 text-stone-700">Original: {formatCurrency(conta.valor || 0)}</Badge>
                              <Badge className="bg-red-100 text-red-700">Total Pago: {formatCurrency((conta.valor || 0) + (conta.juros || 0) + (conta.multa || 0) - (conta.desconto || 0))}</Badge>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
                <Paginacao
                  totalItems={contasFiltradas.length}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                />
              </>
            )}
          </div>
        </TabsContent>

        {/* Calendário */}
        <TabsContent value="calendario" className="mt-6">
          <CalendarioContas
            contasPagar={contasProcessadas}
            contasReceber={lancamentos.filter(l => l.tipo === 'receita' && l.status !== 'pago')}
          />
        </TabsContent>

        {/* Lembretes */}
        <TabsContent value="lembretes" className="mt-6">
          <LembretesPanel
            contas={contasProcessadas}
            onMarcarPago={abrirQuitacao}
            onVerConta={handleEdit}
          />
        </TabsContent>

        {/* Fluxo Futuro */}
        <TabsContent value="fluxo" className="mt-6">
          <FluxoCaixaFuturo
            contas={contasProcessadas}
            saldoAtual={saldoAtual}
          />
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-red-600" />
              {editingConta ? "Editar Conta" : "Nova Conta a Pagar"}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da conta a pagar
            </DialogDescription>
          </DialogHeader>
          <ContaForm
            conta={editingConta}
            onSuccess={handleSuccess}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={refundDialogOpen} onOpenChange={(v) => { setRefundDialogOpen(v); if (!v) setRefundConta(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Estornar baixa</DialogTitle>
            <DialogDescription>Esta operação é crítica. Preencha o motivo e confirme.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Motivo do estorno</Label>
              <Textarea rows={3} placeholder="Descreva o motivo" value={refundMotivo} onChange={(e) => setRefundMotivo(e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="refundConfirm" className="w-4 h-4" checked={refundConfirm} onChange={(e) => setRefundConfirm(e.target.checked)} />
              <label htmlFor="refundConfirm" className="text-sm text-stone-600">Confirmo o estorno desta baixa</label>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setRefundDialogOpen(false); setRefundConta(null); }} className="flex-1">Cancelar</Button>
            <Button
              onClick={async () => {
                if (!refundConfirm || !refundConta) return;
                try {
                  const perm = await agrogestao.functions.invoke('validarAcessoPropriedade', {
                    propriedade_id: refundConta.propriedade_id || localStorage.getItem('propriedade_selecionada'),
                    acao: 'financeiro_estorno'
                  });
                  if (!perm?.data?.success) { return; }
                  const rel = getLancamentoRelacionadoPagar(refundConta);
                  if (rel) {
                    await agrogestao.entities.Lancamento.delete(rel.id);
                  }
                  await updateMutation.mutateAsync({ id: refundConta.id, data: { status: 'pendente', data_pagamento: null, juros: 0, multa: 0, desconto: 0 } });
                  try {
                    const extratos = await agrogestao.entities.ExtratoBancario.filter({ propriedade_id: refundConta.propriedade_id || propriedadeId });
                    const vinculados = (extratos || []).filter(e => String(e.conta_pagar_id) === String(refundConta.id) || String(e.lancamento_id) === String(rel?.id));
                    for (const e of vinculados) {
                      if (e.conta_bancaria_id) {
                        const contasBanco = await agrogestao.entities.ContaBancaria.filter({ id: e.conta_bancaria_id });
                        const contaBanco = contasBanco[0];
                        if (contaBanco) {
                          const novoSaldo = e.tipo === 'debito'
                            ? (contaBanco.saldo_atual || 0) + (e.valor || 0)
                            : (contaBanco.saldo_atual || 0) - (e.valor || 0);
                          await agrogestao.entities.ContaBancaria.update(contaBanco.id, { saldo_atual: novoSaldo });
                        }
                      }
                      await agrogestao.entities.ExtratoBancario.delete(e.id);
                    }
                  } catch { }
                  const u = await agrogestao.auth.me();
                  await agrogestao.client.from('audit_logs').insert({
                    user_email: u?.email || null,
                    propriedade_id: refundConta.propriedade_id || localStorage.getItem('propriedade_selecionada') || null,
                    acao: 'estorno_baixa',
                    table: 'contas_pagar',
                    status: 'success',
                    details: { conta_id: refundConta.id, motivo: refundMotivo, original: { valor: refundConta.valor, juros: refundConta.juros, multa: refundConta.multa, desconto: refundConta.desconto, data_pagamento: refundConta.data_pagamento } }
                  });
                  queryClient.invalidateQueries({ queryKey: ['contas_pagar'] });
                  queryClient.invalidateQueries({ queryKey: ['lancamentos'] });
                  queryClient.invalidateQueries({ queryKey: ['extratos'] });
                  setRefundDialogOpen(false);
                  setRefundConta(null);
                } catch { }
              }}
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={!refundConfirm || !refundMotivo}
            >
              Confirmar estorno
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settleDialogOpen} onOpenChange={(v) => { setSettleDialogOpen(v); if (!v) setSettleConta(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Quitar Conta</DialogTitle>
            <DialogDescription>Informe ajustes e finalize o pagamento</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Juros (R$)</Label>
              <Input type="number" step="0.01" value={settleData.juros} onChange={(e) => setSettleData({ ...settleData, juros: e.target.value })} />
            </div>
            <div>
              <Label>Multa (R$)</Label>
              <Input type="number" step="0.01" value={settleData.multa} onChange={(e) => setSettleData({ ...settleData, multa: e.target.value })} />
            </div>
            <div>
              <Label>Desconto (R$)</Label>
              <Input type="number" step="0.01" value={settleData.desconto} onChange={(e) => setSettleData({ ...settleData, desconto: e.target.value })} />
            </div>
            <div>
              <Label>Forma de Pagamento</Label>
              <Select value={settleData.forma_pagamento} onValueChange={(v) => setSettleData({ ...settleData, forma_pagamento: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                  <SelectItem value="debito_automatico">Débito Automático</SelectItem>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="cartao">Cartão</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Conta Bancária</Label>
              <Select value={settleData.conta_bancaria_id} onValueChange={(v) => setSettleData({ ...settleData, conta_bancaria_id: v === 'none' ? '' : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {contasBancarias.map(cb => (
                    <SelectItem key={cb.id} value={cb.id}>{cb.banco} • {cb.numero}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Data do Pagamento</Label>
              <Input type="date" value={settleData.data_pagamento} onChange={(e) => setSettleData({ ...settleData, data_pagamento: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Comprovante de Pagamento (opcional)</Label>
              <Input type="file" accept="application/pdf,image/*" onChange={handleComprovanteUpload} disabled={uploadingComprovante} />
              {settleData.comprovante_url && (
                <a href={settleData.comprovante_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600">
                  Visualizar comprovante
                </a>
              )}
            </div>
            <div className="col-span-2">
              <Label>Observação</Label>
              <Textarea rows={2} placeholder="Observações da quitação" value={settleData.observacao} onChange={(e) => setSettleData({ ...settleData, observacao: e.target.value })} />
            </div>
          </div>
          <div className="mt-4 p-3 bg-stone-50 rounded-xl border border-stone-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-stone-600">Valor original</p>
              <p className="font-semibold text-stone-800">{formatCurrency(settleConta?.valor || 0)}</p>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-sm text-stone-600">Total a pagar</p>
              <p className="text-lg font-bold text-red-700">
                {formatCurrency((settleConta?.valor || 0) + (parseFloat(settleData.juros) || 0) + (parseFloat(settleData.multa) || 0) - (parseFloat(settleData.desconto) || 0))}
              </p>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setSettleDialogOpen(false); setSettleConta(null); }} className="flex-1">Cancelar</Button>
            <Button onClick={confirmarQuitacao} className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]">Confirmar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
